import type { Mode } from "./match.ts";
import type { SimpatiaRarity } from "./simpatias.ts";

/**
 * Mandinga do Dia (doc 00, O Saci e a Mandinga do Dia; doc 01; doc 02; doc 03; doc 05): ao
 * amanhecer, um modificador global para o dia e a noite seguinte, visível no HUD.
 */
export const MANDINGA_IDS = ["lua-cheia", "chuva-de-caju", "neblina-grossa", "pe-de-vento", "feira-livre", "noite-comprida", "terra-tremendo"] as const;
export type MandingaId = (typeof MANDINGA_IDS)[number];

export interface MandingaDef {
  id: MandingaId;
  name: string;
  /** Linha do anúncio ao amanhecer. */
  hint: string;
}

export const MANDINGAS: Record<MandingaId, MandingaDef> = {
  "lua-cheia": { id: "lua-cheia", name: "Lua Cheia", hint: "lobisomens em dobro e mais fortes à noite" },
  "chuva-de-caju": { id: "chuva-de-caju", name: "Colheita Farta", hint: "as frutas rendem o dobro" },
  "neblina-grossa": { id: "neblina-grossa", name: "Névoa Densa", hint: "a ilha some na névoa" },
  "pe-de-vento": { id: "pe-de-vento", name: "Passo de Pluma", hint: "a gravidade fica mais leve" },
  "feira-livre": { id: "feira-livre", name: "Dia de Feira", hint: "os mercadores dão desconto" },
  "noite-comprida": { id: "noite-comprida", name: "Noite Longa", hint: "a noite dura 50 % a mais" },
  "terra-tremendo": { id: "terra-tremendo", name: "Chuva de Pedras", hint: "os minérios rendem mais e pedras caem do céu" },
};

export const mandingaDef = (id: string): MandingaDef | undefined => (MANDINGAS as Record<string, MandingaDef>)[id];

/** Números de cada Mandinga (hipóteses de playtest, centralizadas aqui). */
export const MANDINGA = {
  /** Lua Cheia: peso do Lobisomem ×2, vida e dano ×1,25 (doc 01). */
  luaCheia: { type: "lobisomem", weight: 2, hp: 1.25, damage: 1.25 },
  /** Chuva de Caju: frutas ×2. */
  fruit: 2,
  /** Neblina Grossa: densidade da neblina (só visual). */
  fog: 5,
  /** Pé de Vento: gravidade ×0,6 (doc 02). */
  gravity: 0.6,
  /** Feira Livre: preços do mascate ×0,7 (doc 06). */
  price: 0.7,
  /** Noite Comprida: noite ×1,5 (doc 01). */
  night: 1.5,
  /** Terra Tremendo: minérios ×1,5 (premissa) e a Pedra Fulminada (doc 03). */
  ore: 1.5,
  meteor: { every: 75, ring: [30, 70] as const, max: 4, warning: 2, hp: 650, tier: 4, drops: [2, 4] as const },
} as const;

/** Estrutura do mundo da Terra Tremendo (a pedra que cai do céu). */
export const MANDINGA_STRUCTURE_NAMES: Record<string, string> = { "pedra-fulminada": "Meteorito" };

/** Item de fruta (a Chuva de Caju dobra) e de minério (a Terra Tremendo aumenta). */
export const FRUIT_ITEMS: ReadonlySet<string> = new Set(["caju", "banana", "acai", "coco"]);
export const ORE_ITEMS: ReadonlySet<string> = new Set(["minerio-ferro", "minerio-prata", "pederneira"]);

/** Fator de rendimento do item colhido na Mandinga do dia. */
export function mandingaYield(mandinga: string, itemId: string): number {
  if (mandinga === "chuva-de-caju" && FRUIT_ITEMS.has(itemId)) return MANDINGA.fruit;
  if (mandinga === "terra-tremendo" && ORE_ITEMS.has(itemId)) return MANDINGA.ore;
  return 1;
}

/** Preço no mascate com a Mandinga: a Feira Livre desconta 30 % nas compras (o que ele paga não muda). */
export function mandingaPrice(price: number, mandinga: string, side: "comprar" | "vender"): number {
  return side === "comprar" && mandinga === "feira-livre" ? Math.max(1, Math.round(price * MANDINGA.price)) : price;
}

/**
 * Sorteia a Mandinga do dia `day` (do dia 1 em diante; o Dia 0 é sem Mandinga): nunca a de ontem,
 * nunca *Noite Comprida* numa noite de chefe (doc 07, L3) nem na Treta (noite fixa, L6).
 */
export function rollMandinga(rand: () => number, day: number, previous: string, bossNight: boolean, mode: Mode): MandingaId | "" {
  if (day < 1) return "";
  const pool = MANDINGA_IDS.filter((id) => id !== previous && !(id === "noite-comprida" && (bossNight || mode === "treta")));
  return pool[Math.floor(rand() * pool.length)] ?? "";
}

/** Travessuras do Saci (doc 06, pesos propostos). */
export const TRAVESSURAS = ["item-cai", "no-na-bota", "invertidos", "arremesso", "troca", "embaralhado", "presente"] as const;
export type Travessura = (typeof TRAVESSURAS)[number];
export const TRAVESSURA_WEIGHTS: Record<Travessura, number> = {
  "item-cai": 22,
  "no-na-bota": 18,
  invertidos: 14,
  arremesso: 14,
  troca: 12,
  embaralhado: 12,
  presente: 8,
};

/** Redemoinhos do Saci (doc 06, bestiário; doc 01; doc 03). */
export const SACI = {
  /** Vagando e fugindo de quem segura a Peneira (m/s). */
  speed: 6,
  flee: 7.5,
  /** Atração leve por jogadores a menos de 40 m; foge de quem está peneirando (premissa: "segura a Peneira" = segurando a captura). */
  attract: 40,
  fleeRadius: 12,
  /** Toque a 2,5 m e recarga de 20 s por jogador. */
  contact: 2.5,
  cooldown: 20,
  /** Captura: segurar 0,8 s a até 3 m com a Peneira na mão (doc 06; o doc 03 dizia 1,5 s). */
  hold: 0.8,
  reach: 3,
  /** A troca de posição avisa (brilho) 0,5 s antes. */
  swapWarning: 0.5,
  /** Efeitos: controles invertidos 8 s; nó na bota 10 s com 60 % da velocidade (premissa); arremesso a 14 m/s para cima. */
  inverted: 8,
  slow: { seconds: 10, factor: 0.6 },
  toss: 14,
  /** O item do cinto cai a 3–5 m. */
  dropDistance: [3, 5] as const,
  /** Presente: 70 % tostões (10–30), 30 % Simpatia Comum (premissa). */
  gift: { coins: [10, 30] as const, simpatiaChance: 0.3 },
  /** Raio do passeio em volta de onde está e o novo destino a cada tanto (s). */
  wander: { radius: 60, every: 8 },
  /** Visível a 60 m (assobio e poeira, no cliente). */
  visible: 60,
} as const;

/** Simpatia da captura: Rara (doc 00). */
export const SACI_CAPTURE: Record<SimpatiaRarity, number> = { comum: 0, rara: 1, lendaria: 0 };

/** Redemoinhos por dia: min(3, 1 + ⌊n/3⌋) (doc 01). */
export const whirlwindCount = (players: number): number => Math.min(3, 1 + Math.floor(Math.max(1, players) / 3));

/** Sorteio ponderado da travessura (`rand` em [0, 1)). */
export function rollTravessura(rand: () => number, allowed: (t: Travessura) => boolean = () => true): Travessura {
  const pool = TRAVESSURAS.filter(allowed);
  let roll = rand() * pool.reduce((s, t) => s + TRAVESSURA_WEIGHTS[t], 0);
  for (const t of pool) {
    roll -= TRAVESSURA_WEIGHTS[t];
    if (roll <= 0) return t;
  }
  return pool[pool.length - 1] ?? "presente";
}
